/**
 * The demo engine's audio graph.
 *
 * One master chain (compressor → gentle low-pass → analyser), a music bus and
 * an SFX bus, a generated convolution reverb, and a looping vinyl-crackle
 * layer that fades in only while a record is actually playing.
 */

import { mulberry32 } from '../../lib/random.ts';

export interface VoiceBus {
  ctx: AudioContext;
  /** Where musical voices land. */
  music: GainNode;
  /** Where one-shot interface sounds land, unaffected by the music bus. */
  sfx: GainNode;
  verb: ConvolverNode;
  noise: AudioBuffer;
}

export interface DemoGraph extends VoiceBus {
  master: GainNode;
  analyser: AnalyserNode;
  levelBuf: Uint8Array<ArrayBuffer>;
  crackle: GainNode;
  crackleSrc: AudioBufferSourceNode;
}

function noiseBuffer(ctx: AudioContext, sec: number): AudioBuffer {
  const b = ctx.createBuffer(1, Math.floor(ctx.sampleRate * sec), ctx.sampleRate);
  const d = b.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  return b;
}

function impulse(ctx: AudioContext, sec: number, decay: number): AudioBuffer {
  const n = Math.floor(ctx.sampleRate * sec);
  const b = ctx.createBuffer(2, n, ctx.sampleRate);
  for (let c = 0; c < 2; c++) {
    const d = b.getChannelData(c);
    for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / n) ** decay;
  }
  return b;
}

/** Continuous vinyl surface noise: hiss with random pops sprinkled through it. */
function crackleBuffer(ctx: AudioContext, len: number): AudioBuffer {
  const b = ctx.createBuffer(1, Math.floor(ctx.sampleRate * len), ctx.sampleRate);
  const d = b.getChannelData(0);
  const rnd = mulberry32(1234);
  for (let i = 0; i < d.length; i++) d[i] = (rnd() * 2 - 1) * 0.16;
  for (let k = 0; k < len * 34; k++) {
    const at = Math.floor(rnd() * (d.length - 600));
    const amp = 0.25 + rnd() * 0.75;
    const dur = 40 + Math.floor(rnd() * 240);
    for (let i = 0; i < dur; i++) {
      d[at + i] = (d[at + i] as number) + (rnd() * 2 - 1) * amp * (1 - i / dur) ** 3;
    }
  }
  return b;
}

function buildCrackle(
  ctx: AudioContext,
  master: GainNode,
): Pick<DemoGraph, 'crackle' | 'crackleSrc'> {
  const src = ctx.createBufferSource();
  src.buffer = crackleBuffer(ctx, 6);
  src.loop = true;
  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 1400;
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 8200;
  const gain = ctx.createGain();
  gain.gain.value = 0;
  src.connect(hp).connect(lp).connect(gain).connect(master);
  src.start();
  return { crackle: gain, crackleSrc: src };
}

export function buildGraph(ctx: AudioContext, volume: number): DemoGraph {
  const master = ctx.createGain();
  master.gain.value = volume;

  const comp = ctx.createDynamicsCompressor();
  comp.threshold.value = -14;
  comp.knee.value = 22;
  comp.ratio.value = 3.2;
  comp.attack.value = 0.006;
  comp.release.value = 0.22;

  const warm = ctx.createBiquadFilter();
  warm.type = 'lowpass';
  warm.frequency.value = 15500;

  const analyser = ctx.createAnalyser();
  analyser.fftSize = 512;
  analyser.smoothingTimeConstant = 0.7;

  master.connect(warm).connect(comp).connect(analyser).connect(ctx.destination);

  const music = ctx.createGain();
  music.connect(master);
  const sfx = ctx.createGain();
  sfx.connect(master);

  const verb = ctx.createConvolver();
  verb.buffer = impulse(ctx, 1.9, 2.6);
  const verbGain = ctx.createGain();
  verbGain.gain.value = 0.26;
  verb.connect(verbGain).connect(master);

  return {
    ctx,
    master,
    music,
    sfx,
    verb,
    analyser,
    levelBuf: new Uint8Array(analyser.fftSize),
    noise: noiseBuffer(ctx, 2),
    ...buildCrackle(ctx, master),
  };
}

/** RMS loudness of whatever is currently passing through the master chain. */
export function readLevel(graph: DemoGraph): number {
  graph.analyser.getByteTimeDomainData(graph.levelBuf);
  const buf = graph.levelBuf;
  let sum = 0;
  for (let i = 0; i < buf.length; i += 2) {
    const v = ((buf[i] as number) - 128) / 128;
    sum += v * v;
  }
  return Math.min(Math.sqrt(sum / (buf.length / 2)) * 3.1, 1);
}
